import { useCallback, useMemo } from 'react';

import { Permission } from '@/shared/lib/secure-json/core/secure-json-collection/types';
import { useSJDatabase } from '@/shared/lib/sj-orm';
import {
  DelegatingRequestDto,
  DelegatingRequestItemDto,
  DelegatingRequestStatus,
} from '@/shared/lib/sj-orm/models/delegating-request.dto';
import { DocumentType } from '@/shared/lib/sj-orm/models/document/document-base.dto';

import { DelegationDto } from '../types';

const mergeDelegatingRequestItemValue = (
  item: DelegatingRequestItemDto,
  patch: Partial<DelegationDto>,
): DelegatingRequestItemDto => {
  if (item.documentType === DocumentType.ASSET_INFORMATION) {
    return {
      ...item,
      value: { ...item.value, ...(patch as Partial<typeof item.value>) },
    };
  }

  if (item.documentType === DocumentType.PRIVATE_DOCUMENTS) {
    return {
      ...item,
      value: { ...item.value, ...(patch as Partial<typeof item.value>) },
    };
  }

  return {
    ...item,
    value: { ...item.value, ...(patch as Partial<typeof item.value>) },
  };
};

const replaceDelegatingRequestItemValue = (
  item: DelegatingRequestItemDto,
  value?: DelegationDto,
): DelegatingRequestItemDto => {
  if (!value) {
    return item;
  }

  if (item.documentType === DocumentType.ASSET_INFORMATION) {
    return {
      ...item,
      value: value as typeof item.value,
    };
  }

  if (item.documentType === DocumentType.PRIVATE_DOCUMENTS) {
    return {
      ...item,
      value: value as typeof item.value,
    };
  }

  return {
    ...item,
    value: value as typeof item.value,
  };
};

export const useDelegatingRequests = () => {
  const sjDb = useSJDatabase();

  const getDelegatingRequests = useCallback(
    (condition?: (item: DelegatingRequestDto) => boolean) => {
      return sjDb.collections.delegatingRequests?.findMany(condition ? condition : () => true);
    },
    [sjDb],
  );

  const getDelegatingRequest = useCallback(
    (id: string) => {
      return sjDb.collections.delegatingRequests?.findOne(
        (delegatingRequest) => delegatingRequest.id === id,
      );
    },
    [sjDb],
  );

  const updateDelegatingRequestValue = useCallback(
    async ({
      requestId,
      patch,
      status,
    }: {
      requestId: string;
      patch: Partial<DelegationDto>;
      status?: DelegatingRequestStatus;
    }) => {
      const request = getDelegatingRequest(requestId);
      const requestItem = request?.items[0];

      if (!request || !requestItem) {
        return undefined;
      }

      return await sjDb.collections.delegatingRequests?.update({
        ...request,
        status: status ?? request.status,
        items: [mergeDelegatingRequestItemValue(requestItem, patch)],
      });
    },
    [getDelegatingRequest, sjDb],
  );

  const setDelegatingRequestStatus = useCallback(
    async ({
      requestId,
      status,
      value,
    }: {
      requestId: string;
      status: DelegatingRequestStatus;
      value?: DelegationDto;
    }) => {
      const request = getDelegatingRequest(requestId);
      const requestItem = request?.items[0];

      if (!request || !requestItem) {
        return undefined;
      }

      return await sjDb.collections.delegatingRequests?.update({
        ...request,
        status,
        items: [replaceDelegatingRequestItemValue(requestItem, value)],
      });
    },
    [getDelegatingRequest, sjDb],
  );

  const grantAssistantAccess = useCallback(
    async (requestId: string, assistantPublicKey: string) => {
      const collection = sjDb.collections.delegatingRequests;
      if (!collection) {
        return;
      }

      await collection.putPublicKey(requestId, assistantPublicKey);
      collection.putPermissions(requestId, assistantPublicKey, [Permission.READ, Permission.WRITE]);
    },
    [sjDb],
  );

  const deleteDelegatingRequests = useCallback(
    (ids: string[]) => {
      return sjDb.collections.delegatingRequests?.removeMany((d) => ids.includes(d.id));
    },
    [sjDb],
  );

  return useMemo(
    () => ({
      getDelegatingRequests,
      getDelegatingRequest,
      updateDelegatingRequestValue,
      setDelegatingRequestStatus,
      grantAssistantAccess,
      isLoading: sjDb.collections.delegatingRequests === undefined,
      deleteDelegatingRequests,
    }),
    [
      getDelegatingRequests,
      getDelegatingRequest,
      updateDelegatingRequestValue,
      setDelegatingRequestStatus,
      grantAssistantAccess,
      sjDb,
      deleteDelegatingRequests,
    ],
  );
};
